import { Straighten as StraightenIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";

import { activityApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { formatDateTime } from "helpers/dateTimeHelper";
import { usePatientActivity } from "hooks/usePatientActivity";
import {
  MetricValueResponseDto,
  MetricValuesPaginationRequestDtoHealthProviderCodeEnum,
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "libs/cellera-sdk";

export interface BodyShapeRow {
  waist?: MetricValueResponseDto;
  hip?: MetricValueResponseDto;
  recordedAt: Date;
}

export const BODY_SHAPE_QUERY_KEY = "activity-body-shape";

interface BodyShapeTabContentProps {
  patientId: string;
}

const BodyShapeTabContent: React.FC<BodyShapeTabContentProps> = ({
  patientId,
}) => {
  const columns: CustomTableColumn<BodyShapeRow>[] = useMemo(
    () => [
      {
        id: "waist",
        label: "Waist Circumference",
        getValue: (row) => {
          const value =
            row.waist?.valueFloat ??
            row.waist?.valueInt ??
            row.waist?.valueText;
          return value == null ? "-" : `${value} in`;
        },
        width: "33.33%",
      },
      {
        id: "hip",
        label: "Hip Circumference",
        getValue: (row) => {
          const value =
            row.hip?.valueFloat ?? row.hip?.valueInt ?? row.hip?.valueText;
          return value == null ? "-" : `${value} in`;
        },
        width: "33.33%",
      },
      {
        id: "recordedAt",
        label: "Recorded Time",
        getValue: (row) => formatDateTime(row.recordedAt),
        width: "33.33%",
      },
    ],
    [],
  );

  const fetchPage = useCallback(
    async (page: number, pageSize: number) => {
      const [waistRes, hipRes] = await Promise.all([
        activityApi.getMetricValuesPaginatedByPatientId({
          patientId,
          metricValuesPaginationRequestDto: {
            page: page + 1,
            pageSize,
            metricCode:
              MetricValuesPaginationRequestDtoMetricCodeEnum.WaistCircumference,
            healthProviderCode:
              MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
            orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
          },
        }),
        activityApi.getMetricValuesPaginatedByPatientId({
          patientId,
          metricValuesPaginationRequestDto: {
            page: page + 1,
            pageSize,
            metricCode:
              MetricValuesPaginationRequestDtoMetricCodeEnum.HipCircumference,
            healthProviderCode:
              MetricValuesPaginationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
            orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
          },
        }),
      ]);

      const merged: BodyShapeRow[] = [];
      const maxLen = Math.max(waistRes.data.length, hipRes.data.length);
      for (let i = 0; i < maxLen; i++) {
        const waist = waistRes.data[i];
        const hip = hipRes.data[i];
        if (!waist && !hip) continue;
        const recordedAt = new Date(
          Math.max(
            waist ? new Date(waist.startDate).getTime() : 0,
            hip ? new Date(hip.startDate).getTime() : 0,
          ),
        );
        merged.push({ waist, hip, recordedAt });
      }

      const total = Math.max(
        waistRes.pagination.total ?? 0,
        hipRes.pagination.total ?? 0,
      );
      return { data: merged, total };
    },
    [patientId],
  );

  const {
    data,
    total,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    isLoading,
    onPageChange,
    onRowsPerPageChange,
  } = usePatientActivity<BodyShapeRow>({
    patientId,
    queryKey: BODY_SHAPE_QUERY_KEY,
    initialPageSize: 10,
    fetchPage,
  });

  return (
    <CustomTable<BodyShapeRow>
      columns={columns}
      loadingMessage="Loading body shape data..."
      emptyStateContent={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <StraightenIcon
            sx={{ color: "text.disabled", fontSize: 48, opacity: 0.5 }}
          />
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            No body shape data available
          </Typography>
        </Box>
      }
      getRowKey={(row) =>
        row.waist?.metricId ||
        row.hip?.metricId ||
        row.recordedAt.getTime().toString()
      }
      data={data}
      totalCount={total}
      page={page}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      isLoading={isLoading}
      tableContainerSx={{
        height: "calc(100vh - 370px)",
      }}
    />
  );
};

export default BodyShapeTabContent;
